import { Avatar, Box, Button, Text } from '@wix/design-system';
import { UserContactsSection } from '@wix/wix-help-widget-common/homepage';
import { useTranslation } from '@wix/wix-i18n-config';
import { Phone } from '@wix/wix-ui-icons-common';
import React, { FC, useMemo } from 'react';

import { experiments } from '../../../experiments/client';
import { SectionComponentProps } from '../types';
import { getFormattedPhoneNumber } from './getFormattedPhoneNumber';
import { userContactsDataHooks } from './UserContacts.dataHooks';
import { UserContactsEmail } from './UserContactsEmail';
import { UserContactsTeam } from './UserContactsTeam';
import { UserContactsTime } from './UserContactsTime';

export const UserContacts: FC<SectionComponentProps<UserContactsSection>> = ({
  profileImageUrl,
  name,
  email,
  phoneNumber,
  timeZone,
  bookASessionLink,
}) => {
  const showBookASession =
    experiments.is('specs.chatbot.accountBasedSuccessManager', 'true') &&
    !!bookASessionLink;
  const { t } = useTranslation();
  const formattedPhoneNumber = useMemo(
    () => getFormattedPhoneNumber(phoneNumber),
    [phoneNumber],
  );

  if (!profileImageUrl && !name && !phoneNumber && !timeZone) {
    return (
      <React.Fragment>
        <UserContactsTeam email={email} />
      </React.Fragment>
    );
  }

  return (
    <Box width="100%" direction="vertical" gap={2} padding="18px">
      <Box gap={2} verticalAlign="middle">
        <Avatar
          size="size36"
          imgProps={{ src: profileImageUrl }}
          name={name}
          dataHook={userContactsDataHooks.avatar()}
        />
        <Text
          size="small"
          weight="bold"
          dataHook={userContactsDataHooks.name()}
        >
          {name}
        </Text>
      </Box>
      <Box gap={2}>
        <Phone fill="#116DFF" size={18} />
        <Text size="small" dataHook={userContactsDataHooks.phoneNumber()}>
          {formattedPhoneNumber}
        </Text>
      </Box>
      <UserContactsEmail email={email} />
      <UserContactsTime timeZone={timeZone} />
      {showBookASession && (
        <Box gap={2}>
          <Button
            size="tiny"
            priority="secondary"
            as="a"
            href={bookASessionLink}
            target="_blank"
            dataHook={userContactsDataHooks.bookASessionLink()}
          >
            {t('home_contact_schedule_a_call')}
          </Button>
        </Box>
      )}
    </Box>
  );
};
